import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  DataTablePagination,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import { useLaneRates } from 'hooks/useLaneRate';
import { t } from 'i18next';

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useModal from 'hooks/useModal';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontal,
} from 'lucide-react';
import AuditTrailModal from 'components/modules/pricing/modals/LaneRateAuditTrailModal';
import { LaneRatesCsvUploadModal } from 'components/modules/pricing/LaneRatesCsvUploadModal';
import { getColumns } from './columns';
import { EditRateSequenceModal } from './EditRateSequenceModal';

const highlighted = {
  rateCustomer: true,
  rateMarket: true,
  rateEquipmentType: true,
  rateEquipmentSize: true,
  rateHazardous: true,
  rateOrderType: true,
  rateSteamshipOrRailroad: true,
};

export default function LaneRatesRateConditions() {
  const columns = getColumns();
  const [hideRateConditionColumns, setHideRateConditionColumns] =
    useState(true);
  const [columnVisibility, setColumnVisibility] = useState({
    rateCustomer: false,
    rateEquipmentType: false,
    rateEquipmentSize: false,
    rateHazardous: false,
    rateOrderType: false,
    rateSteamshipOrRailroad: false,
  });
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageIndex: 0,
  });
  const { visible, show, close } = useModal();
  const [selectedRateSequence, setSelectedRateSequence] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [rateAudit, setRateAudit] = useState(null);
  const [showLaneRatesCsvUploadModal, setLaneRatesCsvUploadModalIsOpen] =
    useState(false);

  const history = useHistory();
  const { data, isLoading } = useLaneRates({ sorting, pagination });

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection).length;
  }, [rowSelection]);

  function handleRowSelectionChange(selection) {
    setRowSelection(selection);
  }

  function handleSortChange(updater) {
    const newValue = updater(sorting);
    setSorting(newValue);
  }

  function onEditRateSequence(laneRate) {
    setSelectedRateSequence(laneRate);
    show();
  }

  function onEditLaneRate(laneRate) {
    history.push(`/lane-rates/${laneRate.id}/edit`);
  }

  function onBulkEditRateSequence() {
    const selectedLaneRates = Object.keys(rowSelection).map((index) => {
      return {
        ...data?.laneRates?.[index],
        weeklyCommitment: data?.laneRates?.[index]?.commitment,
      };
    });
    history.push('/lane-rates/edit', {
      laneRates: selectedLaneRates,
      listQueryFilterParams: {},
    });
  }

  function handleAuditClick(laneRate) {
    setRateAudit(laneRate);
    setShowAuditTrailModal(true);
  }

  function handleBulkUploadClick() {
    setLaneRatesCsvUploadModalIsOpen(true);
  }

  function renderViewRateCard(laneRate) {
    const requiredDoc = laneRate?.document || laneRate?.oldDocument;
    const url = requiredDoc?.signedUrl || '';

    if (requiredDoc && url) {
      return (
        <DropdownMenuItem>
          <Link to={{ pathname: url }} target="_blank" className="text-black">
            {t('VIEW_RATE_CARD')}
          </Link>
        </DropdownMenuItem>
      );
    }
  }

  const mappedColumnDefs = useMemo(() => {
    return columns.concat({
      id: 'actions',
      cell: ({ row }) => {
        const payment = row.original;

        return (
          <div className="text-right">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-8 h-8 p-0">
                  <span className="sr-only">{t('OPEN_MENU')}</span>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t('ACTIONS')}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onEditRateSequence(payment)}>
                  {t('EDIT_RATE_SEQUENCE')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditLaneRate(payment)}>
                  {t('EDIT_LANE_RATE')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {renderViewRateCard(payment)}
                <DropdownMenuItem onClick={() => handleAuditClick(payment)}>
                  {t('VIEW_AUDIT_TRAIL')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });
  }, []);

  function handlePaginationChange(selection) {
    setPagination(selection);
  }
  const options = {
    columns: mappedColumnDefs,
    data: data?.laneRates || [],
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnVisibility,
      sorting,
      pagination,
      rowSelection,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortChange,
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
  };
  const table = useReactTable({
    ...options,
    manualSorting: true,
    manualPagination: true,
    rowCount: data?.count,
  });

  useEffect(() => {
    if (hideRateConditionColumns) {
      setColumnVisibility({
        rateCustomer: false,
        rateEquipmentType: false,
        rateEquipmentSize: false,
        rateHazardous: false,
        rateOrderType: false,
        rateSteamshipOrRailroad: false,
      });
    } else {
      setColumnVisibility({
        rateCustomer: true,
        rateEquipmentType: true,
        rateEquipmentSize: true,
        rateHazardous: true,
        rateOrderType: true,
        rateSteamshipOrRailroad: true,
      });
    }
  }, [hideRateConditionColumns]);

  function renderFooter() {
    if (isLoading) {
      return null;
    }
    return <DataTablePagination table={table} />;
  }

  function onToggleRateConditionsVisibility() {
    setHideRateConditionColumns((visibility) => !visibility);
  }

  function renderHeader() {
    return (
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{t('ACCESSORIAL_RATES')}</CardTitle>
          <div className="flex justify-end">
            <div className="flex space-x-4">
              <Button
                onClick={onToggleRateConditionsVisibility}
                className={[
                  'h-10',
                  {
                    'bg-slate-50': !hideRateConditionColumns,
                  },
                ]}
                size="sm"
                variant="outline"
              >
                {hideRateConditionColumns
                  ? t('EXPAND_RATE_CONDITIONS')
                  : t('COLLAPSE_RATE_CONDITIONS')}
                {hideRateConditionColumns ? (
                  <ChevronRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronLeftIcon className="w-4 h-4" />
                )}
              </Button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10">
                    {t('ACTIONS')} <ChevronDownIcon className="ml-2 w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleBulkUploadClick()}>
                    {t('UPLOAD_LANE_RATES')}
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Link to="/lane-rates/add" className="text-black">
                      {t('ADD_NEW_LANE_RATE')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem>{t('EXPORT_LANE_RATES')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        {selectedRowCount > 0 && (
          <Alert className="mt-4 text-sm bg-blue-50">
            <div className="flex justify-between items-center">
              <span>
                {selectedRowCount} {t('ROWS_SELECTED')}.
              </span>
              <Button variant="link" onClick={() => onBulkEditRateSequence()}>
                {t('EDIT_LANE_RATES')}
                <ArrowRightIcon className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </Alert>
        )}
      </CardHeader>
    );
  }

  return (
    <div className="px-4 py-5 sm:p-6 mx-auto">
      <Card>
        {renderHeader()}
        <CardContent className="space-y-4">
          <DataTable
            table={table}
            isLoading={isLoading}
            highlighted={highlighted}
          />
          {renderFooter()}
        </CardContent>
      </Card>
      <AuditTrailModal
        show={showAuditTrailModal}
        rate={rateAudit}
        onHide={() => setShowAuditTrailModal(false)}
        type="laneRates"
      />
      {showLaneRatesCsvUploadModal && (
        <LaneRatesCsvUploadModal
          onClose={() => setLaneRatesCsvUploadModalIsOpen(false)}
        />
      )}
      {visible && (
        <EditRateSequenceModal
          laneRate={selectedRateSequence}
          open={visible}
          onOpenChange={close}
        />
      )}
    </div>
  );
}
